import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '@/redux/common/baseQuery';

interface BrandConfig {
  primary_color: string;
  accent_color: string;
  success_color: string;
  logo_light_url: string;
  logo_dark_url: string;
  favicon_url: string;
}

interface ApiResponse<T> {
  success: boolean;
  message_code: number;
  message: string;
  data: T;
}

interface UploadResponse {
  url: string;
}

export const brandApi = createApi({
  reducerPath: 'brandApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Brand'],
  endpoints: (builder) => ({
    getBrandConfig: builder.query<BrandConfig, void>({
      query: () => '/api/v1/configurations/brand',
      transformResponse: (res: ApiResponse<{ effective_value: BrandConfig }>) =>
        res.data?.effective_value ?? ({} as BrandConfig),
      providesTags: ['Brand'],
    }),

    updateBrandConfig: builder.mutation<void, Partial<BrandConfig>>({
      query: (value) => ({
        url: '/api/v1/configurations/brand',
        method: 'PUT',
        body: { value },
      }),
      // Optimistically patch the cache so ThemeLoader re-applies the new color immediately
      // instead of re-fetching stale data after invalidation (which caused the theme to revert
      // until all wizard steps were done and the fetch completed).
      async onQueryStarted(patch, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          brandApi.util.updateQueryData('getBrandConfig', undefined, (draft) => {
            Object.assign(draft, patch);
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    uploadLogo: builder.mutation<string, FormData>({
      query: (formData) => ({
        url: '/api/v1/uploads/logo',
        method: 'POST',
        body: formData,
      }),
      transformResponse: (res: ApiResponse<UploadResponse>) => res.data.url,
    }),
  }),
});

export const { useGetBrandConfigQuery, useUpdateBrandConfigMutation, useUploadLogoMutation } =
  brandApi;
